// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, Download, CheckCircle, AlertCircle, RefreshCw, MonitorSmartphone } from 'lucide-react';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import './PWAStatus.css';

function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return iOS && !window.MSStream;
}

/**
 * État réel de l'application installable : connexion, application installée,
 * disponibilité hors ligne et mise à jour en attente.
 *
 * Ce composant n'enregistre AUCUN service worker : `index.js` le fait une
 * seule fois. Il s'abonne à l'état publié par `serviceWorkerRegistration`.
 */
const PWAStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [etatSW, setEtatSW] = useState(serviceWorkerRegistration.etatServiceWorker());
  const [updateDismissed, setUpdateDismissed] = useState(false);
  // « Installée » = ouverte en fenêtre autonome (display-mode: standalone).
  const [isInstalled, setIsInstalled] = useState(false);
  const [cacheInfo, setCacheInfo] = useState({});
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(false);

  useEffect(() => {
    // Écouter les changements de connectivité
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Vérifier si l'app est installée
    const checkInstallation = () => {
      const mq = window.matchMedia?.('(display-mode: standalone)');
      if (mq?.matches) {
        setIsInstalled(true);
      }
    };

    checkInstallation();

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => setIsInstalled(true);
    window.addEventListener('appinstalled', onInstalled);

    // Obtenir les informations du cache
    serviceWorkerRegistration
      .getCacheInfo()
      .then(setCacheInfo)
      .catch(() => setCacheInfo({}));

    // État du service worker (enregistré par index.js, jamais ici).
    const desabonner = serviceWorkerRegistration.ecouterEtatServiceWorker(setEtatSW);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      desabonner();
    };
  }, []);

  const showUpdatePrompt = etatSW.miseAJourDisponible && !updateDismissed;

  const handleInstallApp = useCallback(async () => {
    if (!installPrompt) return;
    try {
      await installPrompt.prompt();
      await installPrompt.userChoice;
    } catch {
      /* ignoré */
    } finally {
      setInstallPrompt(null);
    }
  }, [installPrompt]);

  const handleUpdate = () => {
    // Active la version en attente ; la page se recharge quand elle a la main.
    serviceWorkerRegistration.appliquerMiseAJour();
    setUpdateDismissed(true);
  };

  const handleClearCache = async () => {
    await serviceWorkerRegistration.clearCache();
    const info = await serviceWorkerRegistration.getCacheInfo();
    setCacheInfo(info);
  };

  const getStatusIcon = () => {
    if (!isOnline) {
      return <WifiOff size={16} className="status-icon offline" />;
    }
    if (isInstalled) {
      return <CheckCircle size={16} className="status-icon installed" />;
    }
    return <Wifi size={16} className="status-icon online" />;
  };

  const getStatusText = () => {
    if (!isOnline) {
      return etatSW.horsLigneDisponible
        ? 'Hors ligne — application disponible'
        : 'Hors ligne';
    }
    if (isInstalled) {
      return 'Application installée';
    }
    return etatSW.horsLigneDisponible ? 'En ligne — disponible hors ligne' : 'En ligne';
  };

  const getStatusClass = () => {
    if (!isOnline) return 'status-offline';
    if (isInstalled) return 'status-installed';
    return 'status-online';
  };

  return (
    <div className="pwa-status tour-pwa-status">
      {/* Indicateur de statut principal */}
      <div className={`status-indicator ${getStatusClass()}`} role="status" data-testid="pwa-statut">
        {getStatusIcon()}
        <span className="status-text">{getStatusText()}</span>
      </div>

      {/* Informations du cache */}
      {Object.keys(cacheInfo).length > 0 && (
        <div className="cache-info">
          <span className="cache-label">Cache:</span>
          {Object.entries(cacheInfo).map(([name, count]) => (
            <span key={name} className="cache-item">
              {name}: {count}
            </span>
          ))}
        </div>
      )}

      {/* Boutons d'action */}
      <div className="pwa-actions">
        <button
          onClick={handleClearCache}
          className="action-btn clear-cache"
          title="Vider le cache"
          aria-label="Vider le cache"
          type="button"
        >
          <RefreshCw size={16} aria-hidden />
        </button>
      </div>

      {/* Installation PWA (Chrome / Edge) — « extension » navigateur */}
      {!isInstalled && installPrompt && !installDismissed && (
        <div className="pwa-install-banner" role="status">
          <MonitorSmartphone size={16} className="pwa-install-banner__icon" aria-hidden />
          <span className="pwa-install-banner__text">Installer Biblio-Tech</span>
          <button type="button" className="pwa-install-btn" onClick={handleInstallApp}>
            <Download size={16} />
            Installer
          </button>
          <button
            type="button"
            className="pwa-install-dismiss"
            onClick={() => setInstallDismissed(true)}
            aria-label="Masquer"
          >
            ×
          </button>
        </div>
      )}

      {!isInstalled && !installPrompt && isIosSafari() && !installDismissed && (
        <div className="pwa-install-hint" role="note">
          <span>Safari : Partager → Sur l’écran d’accueil pour ajouter l’app.</span>
          <button type="button" className="pwa-install-dismiss" onClick={() => setInstallDismissed(true)} aria-label="Masquer">
            ×
          </button>
        </div>
      )}

      {/* Notification de mise à jour */}
      {showUpdatePrompt && (
        <div className="update-prompt" role="status">
          <div className="update-content">
            <AlertCircle size={20} className="update-icon" aria-hidden />
            <span>Nouvelle version disponible</span>
            <button type="button" onClick={handleUpdate} className="update-btn">
              <Download size={16} aria-hidden />
              Mettre à jour
            </button>
            <button
              type="button"
              onClick={() => setUpdateDismissed(true)}
              className="dismiss-btn"
              aria-label="Plus tard"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Indicateur de mode hors ligne */}
      {!isOnline && (
        <div className="offline-banner">
          <WifiOff size={16} />
          <span>
            Mode hors ligne : la bibliothèque, la recherche et l’IA ont besoin du réseau.
          </span>
        </div>
      )}
    </div>
  );
};

export default PWAStatus;
